import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationType, data, layer, Map, source } from 'azure-maps-control';
import { CoordinateAbbreviated } from '../../models';
import { AzureMapsAuthService } from '../../services';


/**
* Map component to display selected hospital/company coordinates.
*/
@Component({
	selector: 'app-map',
	templateUrl: './map.component.html',
	styleUrls: ['./map.component.css'],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class MapComponent implements OnInit, OnDestroy {

	private readonly azureMapAuthSrv = inject(AzureMapsAuthService);

	private readonly _updateMapEvent = () => this._updateMap();

	@Input() set position(position: CoordinateAbbreviated | undefined) {
		this._position = position;
		if (!!this.map && !!this._position) {
			this.map.events.add('ready', this._updateMapEvent);
		}
	}

	get position(): CoordinateAbbreviated | undefined { return this._position; }

	map?: Map;

	private _position?: CoordinateAbbreviated;
	private readonly zoom: number = 10;
	private readonly sourceId: string = 'SOURCE_ID';
	private readonly layerId: string = 'LAYER_ID';
	private readonly orthofixPosition = {
		lat: 45.44,
		lon: 10.86
	};

	ngOnInit(): void {
		this.initMap();
	}

	private async initMap(): Promise<void> {
		await this.azureMapAuthSrv.initTokenRenewal();

		this._position = this._position || this.orthofixPosition;

		this.map = new Map('myMap', {
			center: new data.Position(this._position.lon!, this._position.lat!),
			zoom: this.zoom,
			autoResize: true,
			interactive: false, // disable user interaction
			enableAccessibility: false, // remove text under map
			showLogo: false,
			authOptions: {
				authType: AuthenticationType.sas,
				getToken: (resolve, reject) => {
					const token = this.azureMapAuthSrv.getCurrentToken();
					if (token) {
						resolve(token);
					} else {
						this.azureMapAuthSrv.fetchToken().then(t => resolve(t ?? undefined)).catch(reject);
					}
				}
			}
		});

		this.map.events.add('error', (e) => console.error('Errore mappa:', e));
		this.map.events.add('ready', this._updateMapEvent);
	}

	/**
	* Update map according selected coordinates.
	*/
	private _updateMap(): void {

		// clear previous source with related layer
		if (this.map!.sources.getById(this.sourceId) != null) {
			this.map!.layers.remove(this.layerId);
			this.map!.sources.remove(this.sourceId);
		}

		// initialize new source
		let datasource = new source.DataSource(this.sourceId);
		this.map!.sources.add(datasource);

		// add new layer
		const resultLayer = new layer.SymbolLayer(datasource, this.layerId, {
			iconOptions: {
				image: 'pin-darkblue',
				anchor: 'center',
			}
		});

		this.map!.layers.add(resultLayer);

		// add new shape and set camera
		const userPosition = new data.Position(this._position!.lon!, this._position!.lat!);
		const userPoint = new data.Point(userPosition)
		const feature = new data.Feature(userPoint);
		datasource.add(feature);
		this.map!.setCamera({
			center: userPosition,
			zoom: this.zoom,
		});

		this.map!.resize();

		this.map!.events.remove('ready', this._updateMapEvent);
	}


	ngOnDestroy(): void {
		this.map?.dispose();
		this.azureMapAuthSrv.clearRenewal();
	}

}
